/**
 * Folha "Nova enquete": a pergunta, de 2 a 8 opções (texto livre, ou um
 * lugar pela busca — a atividade herda o lugar), e a hora de encerrar.
 * Qualquer membro cria; todos recebem por push.
 */
import { uuidv7 } from '@nv/core';
import { useState } from 'react';

import type { DiaDaViagem } from '../../trip/dias';
import { useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { LocalField, type LugarDaAtividade } from '../roteiro/LocalField';
import { enqueteApi, useInvalidarEnquetes } from './api';
import { Sheet } from '../../ui/Sheet';

const MAX_OPCOES = 8;

interface Opcao {
  id: string;
  text: string;
  lugar: LugarDaAtividade | null;
}

/** `YYYY-MM-DDTHH:MM` local, para o `datetime-local`. */
function localDaquiA(min: number): string {
  const d = new Date(Date.now() + min * 60_000);
  d.setSeconds(0, 0);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const novaOpcao = (): Opcao => ({ id: uuidv7(), text: '', lugar: null });

export function NovaEnquete({ dia, dias, onFechar }: { dia: DiaDaViagem; dias?: DiaDaViagem[]; onFechar: () => void }) {
  const { tripId } = useTrip();
  const avisar = useInvalidarEnquetes(tripId);
  const [dayId, setDayId] = useState(dia.id);
  const [pergunta, setPergunta] = useState('');
  const [opcoes, setOpcoes] = useState<Opcao[]>([novaOpcao(), novaOpcao()]);
  const [fecha, setFecha] = useState(localDaquiA(120));
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const validas = opcoes.filter((o) => o.text.trim().length > 0);
  const fechaEm = new Date(fecha);
  const pode = pergunta.trim().length > 0 && validas.length >= 2 && Number.isFinite(fechaEm.getTime()) && fechaEm.getTime() > Date.now();

  function mudar(id: string, text: string, lugar: LugarDaAtividade | null) {
    setOpcoes((lista) => lista.map((o) => (o.id === id ? { ...o, text, lugar } : o)));
  }

  async function criar() {
    if (!pode) return;
    setEnviando(true);
    setErro(null);
    try {
      await enqueteApi.criar(tripId, {
        id: uuidv7(),
        dayId,
        question: pergunta.trim(),
        closesAt: fechaEm.toISOString(),
        options: validas.map((o, i) => ({ id: o.id, text: o.text.trim(), sortOrder: i, lugar: o.lugar })),
      });
      avisar();
      onFechar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível criar agora.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Sheet titleId="enq-nova" onFechar={onFechar}>
        <h2 className="sheet__title" id="enq-nova">Nova enquete</h2>
        {dias && dias.length > 1 && (
          <label className="field">
            <span className="field__label">Dia</span>
            <select className="field__input" value={dayId} onChange={(e) => setDayId(e.target.value)} aria-label="Dia">
              {dias.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.date.slice(8, 10)}/{d.date.slice(5, 7)}{d.title ? ` · ${d.title}` : ''}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span className="field__label">Pergunta</span>
          <input className="field__input" value={pergunta} onChange={(e) => setPergunta(e.target.value)} placeholder="Onde vamos jantar hoje?" maxLength={140} aria-label="Pergunta" />
        </label>
        {opcoes.map((o, i) => (
          <div className="enq__nova-opcao" key={o.id}>
            <LocalField rotulo={`OPÇÃO ${i + 1}`} nome={o.text} lugar={o.lugar} onChange={(nome, l) => mudar(o.id, nome, l)} placeholder="Texto livre ou um lugar" />
            {opcoes.length > 2 && (
              <button type="button" className="enq__tirar" aria-label={`Tirar opção ${i + 1}`} onClick={() => setOpcoes((lista) => lista.filter((x) => x.id !== o.id))}>
                <Icon name="excluir" size={16} />
              </button>
            )}
          </div>
        ))}
        {opcoes.length < MAX_OPCOES && (
          <button type="button" className="nv-btn nv-btn--chip" onClick={() => setOpcoes((lista) => [...lista, novaOpcao()])}>
            <Icon name="criar" size={16} /> Mais uma opção
          </button>
        )}
        <label className="field">
          <span className="field__label">Encerra em</span>
          <input className="field__input" type="datetime-local" value={fecha} onChange={(e) => setFecha(e.target.value)} aria-label="Encerra em" />
        </label>
        <div className="chip-row">
          {[
            [30, 'em 30 min'],
            [60, 'em 1 h'],
            [120, 'em 2 h'],
            [240, 'em 4 h'],
          ].map(([m, rotulo]) => (
            <button type="button" key={m} className="pick" onClick={() => setFecha(localDaquiA(Number(m)))}>
              {rotulo}
            </button>
          ))}
        </div>
        <p className="sheet__text">Um voto por pessoa, que pode mudar até encerrar. Todo mundo recebe um aviso agora e outro 30 min antes de fechar.</p>
        {erro && <p className="auth-error" role="alert">{erro}</p>}
        <div className="fin__ctas">
          <button type="button" className="nv-btn nv-btn--secondary" onClick={onFechar}>Cancelar</button>
          <button type="button" className="nv-btn nv-btn--primary" disabled={!pode || enviando} onClick={() => void criar()}>
            {enviando ? 'Criando…' : 'Criar e avisar todos'}
          </button>
        </div>
    </Sheet>
  );
}
